import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle2, CircleCheck, CircleHelp, CircleOff, Eye, EyeOff, Flag, Loader2, Map as MapIcon, MapPinOff, Pencil,
  Plus, RefreshCw, SearchX, Tags, Trash2, TriangleAlert, X, type LucideIcon,
} from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { fmtDate, fmtInt } from '../constants';
import { COUNTRY_FILTERS, foundedSet, isCountryFilter, type CountryFilterKey } from '../useWorldsStats';
import { withGameGroup } from '../sharedMap';
import type { Country, PossessionItem } from '../worlds.types';
import { BulkRegionModal } from '../components/BulkRegionModal';
import { confirmAction } from '../components/ConfirmDialog';
import { CountryModal } from '../components/CountryModal';
import { FlagThumb } from '../components/FlagThumb';
import { RosterSyncBadge } from '../components/RosterSyncBadge';
import {
  ActionButton, ColorChip, Dash, EmptyRow, FilterChips, InfoBox, SearchField, StatusBadge, TintBadge, cx, type Tone,
} from '../components/ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import cs from '../components/countries.module.scss';

// ============================================================
// Onglet Pays : table, création / édition, activation, f home, région en masse, possession en jeu
// ============================================================
// La possession (qui a fondé quel pays) vit dans NGCountryAPI et n'est affichée qu'en lecture ;
// elle n'est jamais copiée dans Worlds.
//
// Filtre : ?filtre=<clé> (COUNTRY_FILTERS de useWorldsStats, mêmes comptes que l'accueil). L'URL
// est la source du filtre : l'accueil y mène directement, et le retour arrière le restaure.
// ============================================================

type FilterKey = 'tous' | CountryFilterKey;

function matches(c: Country, q: string): boolean {
  if (!q) return true;
  const s = q.toLowerCase();
  return [c.slug, c.slugEn, c.empireNameFr, c.empireNameEn, c.isoCode].some((v) => !!v && v.toLowerCase().includes(s));
}

interface InGame { text: string; tone: Tone; icon: LucideIcon; title?: string }

function inGame(item: PossessionItem | undefined): InGame {
  if (!item) return { text: 'Absent', tone: 'off', icon: CircleOff, title: 'Absent du roster de NGCountryAPI (inactif, sans f home, ou pas encore synchronisé)' };
  if (item.takenByFactionId) {
    const at = item.takenAt ? fmtDate(new Date(item.takenAt).toISOString()) : '';
    return { text: 'Fondé', tone: 'warn', icon: Flag, title: `Fondé par la faction ${item.takenByFactionId}${at ? `, le ${at}` : ''}` };
  }
  if (!item.listed) return { text: 'Non listé', tone: 'off', icon: EyeOff, title: 'Connu de NGCountryAPI mais retiré du roster fondable' };
  return { text: 'Fondable', tone: 'ok', icon: CircleCheck, title: 'Proposé à la fondation en jeu' };
}

type RowBusy = { id: string; what: 'toggle' | 'center' | 'delete' } | null;

/**
 * @param syncReadOnly synchro du roster interdite ; par défaut `readOnly`. Sur la carte commune d'un
 *                     suiveur (Iris), la page est en lecture seule mais la synchro vers la
 *                     NGCountryAPI du groupe reste permise à qui a le droit d'écriture.
 */
export function CountriesTab({ readOnly, syncReadOnly = readOnly, onShowOnMap }: {
  readOnly: boolean;
  syncReadOnly?: boolean;
  onShowOnMap: (id: string) => void;
}) {
  const countries = useWorldsStore((s) => s.countries);
  const countriesVersion = useWorldsStore((s) => s.countriesVersion);
  const geoRegions = useWorldsStore((s) => s.geoRegions);
  const levelsCount = useWorldsStore((s) => s.levels.length);
  const possession = useWorldsStore((s) => s.possession);
  const loadPossession = useWorldsStore((s) => s.loadPossession);
  const putCountry = useWorldsStore((s) => s.putCountry);
  const dropCountry = useWorldsStore((s) => s.dropCountry);
  const syncGrid = useWorldsStore((s) => s.syncGrid);
  const reloadGeoRegions = useWorldsStore((s) => s.reloadGeoRegions);

  const [params, setParams] = useSearchParams();
  const filterParam = params.get('filtre');
  const filter: FilterKey = isCountryFilter(filterParam) ? filterParam : 'tous';

  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<Country | 'new' | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState<RowBusy>(null);
  const [possessionBusy, setPossessionBusy] = useState(false);

  useEffect(() => { void loadPossession(); }, [loadPossession]);

  const setFilter = (k: FilterKey) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'pays');
      if (k === 'tous') next.delete('filtre'); else next.set('filtre', k);
      return next;
    }, { replace: true });
  };

  const goRegions = () => setParams(withGameGroup({ tab: 'regions' }, params));

  const loaded = countriesVersion !== -1;
  const founded = useMemo(() => foundedSet(possession), [possession]);
  const regionById = useMemo(() => new Map(geoRegions.map((r) => [r.id, r])), [geoRegions]);
  const possessionByName = useMemo(() => new Map((possession?.items ?? []).map((i) => [i.name, i])), [possession]);
  const activeFilter = COUNTRY_FILTERS.find((f) => f.key === filter);

  const visible = useMemo(() => {
    const term = q.trim();
    const ctx = { founded };
    return countries.filter((c) => matches(c, term) && (!activeFilter || activeFilter.test(c, ctx)));
  }, [countries, q, activeFilter, founded]);

  const chips = useMemo(() => {
    const ctx = { founded };
    const items: { key: FilterKey; label: string; count?: number; title?: string }[] = [
      { key: 'tous', label: 'Tous', count: countries.length },
    ];
    for (const f of COUNTRY_FILTERS) {
      const unknown = f.needsPossession && !founded;
      if (unknown && f.key !== filter) continue;
      items.push({
        key: f.key,
        label: f.label,
        count: unknown ? undefined : countries.reduce((n, c) => n + (f.test(c, ctx) ? 1 : 0), 0),
        title: unknown ? 'Possession en jeu inconnue : filtre sans effet' : undefined,
      });
    }
    return items;
  }, [countries, founded, filter]);

  const totalChunks = useMemo(() => countries.reduce((n, c) => n + c.chunkCount, 0), [countries]);
  const enabledCount = useMemo(() => countries.reduce((n, c) => n + (c.enabled ? 1 : 0), 0), [countries]);

  // Sélection : seuls les pays encore présents comptent (suppression, rechargement).
  const selectedCountries = useMemo(() => countries.filter((c) => selected.has(c.id)), [countries, selected]);
  const visibleSelected = visible.reduce((n, c) => n + (selected.has(c.id) ? 1 : 0), 0);
  const allVisibleSelected = visible.length > 0 && visibleSelected === visible.length;
  const someVisibleSelected = visibleSelected > 0 && !allVisibleSelected;
  const hiddenSelected = selectedCountries.length - visibleSelected;

  const refreshPossession = async () => {
    setPossessionBusy(true);
    await loadPossession();
    setPossessionBusy(false);
  };

  const patch = async (c: Country, what: 'toggle' | 'center', body: Parameters<typeof api.patchCountry>[1], okMsg: string) => {
    setBusy({ id: c.id, what });
    const res = await api.patchCountry(c.id, body);
    setBusy(null);
    if (!res.data) { toast.error(res.error ?? 'Modification en échec'); return; }
    putCountry(res.data);
    toast.success(okMsg);
  };

  const toggleEnabled = (c: Country) => void patch(c, 'toggle', { enabled: !c.enabled }, c.enabled
    ? `${c.slug} désactivé : retiré du roster fondable, frontières masquées`
    : `${c.slug} activé${c.centerX == null ? ' (sans f home : pas encore fondable)' : ' : de nouveau proposé à la fondation'}`);

  const clearCenter = async (c: Country) => {
    if (c.centerX == null || c.centerZ == null) return;
    const precise = c.centerY != null
      ? ` (Y ${c.centerY}${c.centerYaw != null ? `, yaw ${Math.round(c.centerYaw)}, pitch ${Math.round(c.centerPitch ?? 0)}` : ''})`
      : '';
    const ok = await confirmAction({
      title: `Supprimer le f home de ${c.slug} ?`,
      message: <p>Position actuelle : <strong className={ws.mono}>{c.centerX}, {c.centerZ}</strong>{precise}.</p>,
      details: (
        <>
          Le pays ne sera plus proposé à la fondation tant qu'un staff n'aura pas reposé un point d'apparition
          avec <code>/f setdhome</code> en jeu (ou ici). Les chunks peints et une faction déjà installée ne sont pas touchés.
        </>
      ),
      confirmLabel: 'Supprimer le f home',
    });
    if (!ok) return;
    void patch(c, 'center', { centerX: null, centerY: null, centerZ: null, centerYaw: null, centerPitch: null },
      `F home de ${c.slug} supprimé : pays non fondable jusqu'au prochain /f setdhome`);
  };

  /**
   * Suppression. Le satellite relit la possession SANS cache au moment du DELETE et décide seul :
   * lisible et pays libre, suppression définitive (chunks de tous les niveaux effacés) ; fondé,
   * refus 409 ; illisible, simple désactivation. On relit donc la possession avant d'ouvrir la
   * confirmation, et le texte reste conditionnel quand elle est illisible (elle peut redevenir
   * lisible d'ici l'envoi). Le bouton d'un DELETE s'appelle toujours « Supprimer le pays ».
   */
  const remove = async (c: Country) => {
    setBusy({ id: c.id, what: 'delete' });
    await loadPossession();
    setBusy(null);
    const fresh = useWorldsStore.getState().possession;
    const known = !!fresh?.available;
    const item = known ? fresh?.items.find((i) => i.name === c.slug) : undefined;
    const name = (
      <>
        <strong>{c.slug}</strong>{c.slugEn && c.slugEn !== c.slug ? ` (${c.slugEn})` : ''}
      </>
    );
    const chunksLine = (lead: string, verb: string) => (c.chunkCount > 0 ? (
      <>
        {lead} <strong>{fmtInt(c.chunkCount)}</strong> chunks {verb} effacés de la carte
        {levelsCount > 1 ? ', ainsi que ceux des autres niveaux' : ''}
      </>
    ) : null);

    if (item?.takenByFactionId) {
      // Fondé en jeu : le satellite refuserait (409). On propose la seule action possible.
      if (!c.enabled) {
        toast.warning(`${c.slug} est fondé en jeu (faction ${item.takenByFactionId}) : suppression refusée. Il est déjà inactif.`);
        return;
      }
      const deactivate = await confirmAction({
        title: `${c.slug} est fondé en jeu`,
        tone: 'primary',
        message: <p>{name} appartient à la faction <strong>{item.takenByFactionId}</strong> : le satellite refuse de le supprimer.</p>,
        details: (
          <>
            Tu peux le <strong>désactiver</strong> : il quitte le roster fondable et ses frontières sont masquées ;
            chunks, faction et f home sont conservés.
          </>
        ),
        confirmLabel: 'Désactiver le pays',
      });
      if (deactivate) toggleEnabled(c);
      return;
    }

    const ok = await confirmAction({
      title: `Supprimer le pays ${c.slug} ?`,
      message: known ? (
        <p>{name} sera supprimé définitivement.</p>
      ) : (
        <p>
          La possession en jeu est illisible ({fresh?.message ?? 'NGCountryAPI injoignable'}) : le satellite la relira
          à l'envoi, et le résultat dépend de ce qu'il lira pour {name}.
        </p>
      ),
      details: known ? (
        <ul>
          {c.chunkCount > 0 && <li>{chunksLine('Ses', 'seront')}.</li>}
          <li>Si le pays est fondé d'ici l'envoi, la suppression sera refusée.</li>
        </ul>
      ) : (
        <ul>
          <li>
            Toujours illisible :{' '}
            {c.enabled
              ? <>le pays est seulement <strong>désactivé</strong> (chunks, faction et f home conservés ; il quitte le roster fondable).</>
              : <>aucune modification, le pays est déjà inactif.</>}
          </li>
          <li>
            Redevenue lisible : le pays est <strong>supprimé définitivement</strong>
            {c.chunkCount > 0 ? <> et {chunksLine('ses', 'sont')}</> : null}, ou la suppression est refusée s'il est fondé.
          </li>
        </ul>
      ),
      confirmLabel: 'Supprimer le pays',
    });
    if (!ok) return;
    setBusy({ id: c.id, what: 'delete' });
    const res = await api.deleteCountry(c.id);
    setBusy(null);
    if (!res.data) { toast.error(res.error ?? 'Suppression en échec'); return; }
    if (res.data.disabled) {
      putCountry({ ...c, enabled: false });
      toast.warning(res.data.message);
      return;
    }
    dropCountry(c.id);
    setSelected((s) => { if (!s.has(c.id)) return s; const n = new Set(s); n.delete(c.id); return n; });
    toast.success(`${c.slug} supprimé${res.data.clearedChunks ? `, ${fmtInt(res.data.clearedChunks)} chunks effacés` : ''}`);
    if (res.data.clearedChunks) void syncGrid();
    if (c.geoRegionId) void reloadGeoRegions();
  };

  const toggleOne = (id: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allVisibleSelected) visible.forEach((c) => n.delete(c.id));
    else visible.forEach((c) => n.add(c.id));
    return n;
  });

  const onBulkDone = (ids: string[], geoRegionId: string | null) => {
    const byId = new Map(countries.map((c) => [c.id, c]));
    ids.forEach((id) => {
      const c = byId.get(id);
      if (c && c.geoRegionId !== geoRegionId) putCountry({ ...c, geoRegionId });
    });
    setSelected(new Set());
    void reloadGeoRegions();
  };

  const clearFilters = () => { setQ(''); setFilter('tous'); };

  const colSpan = readOnly ? 10 : 11;
  const filtered = q.trim() !== '' || filter !== 'tous';

  return (
    <div className={ws.tabBody}>
      <div className={ws.toolbar}>
        <span className={ws.counter}>
          <strong>{fmtInt(visible.length)}</strong> pays
          {visible.length !== countries.length && <> sur <strong>{fmtInt(countries.length)}</strong></>}
          {' '}· <strong>{fmtInt(enabledCount)}</strong> actifs
          {totalChunks > 0 && <> · <strong>{fmtInt(totalChunks)}</strong> chunks assignés</>}
        </span>
        <RosterSyncBadge readOnly={syncReadOnly} />
      </div>

      <div className={ws.toolbar}>
        <div className={cs.toolbarMain}>
          <SearchField value={q} onChange={setQ} placeholder="Rechercher (nom, EN, empire, ISO)" className={cs.search} />
          <button type="button" className={styles.secondaryBtn} onClick={() => void refreshPossession()} disabled={possessionBusy}
            title={`Relire la possession en jeu (NGCountryAPI)${possession?.fetchedAt ? `, lue le ${fmtDate(possession.fetchedAt)}` : ''}`}>
            {possessionBusy ? <Loader2 size={14} className={ws.spinning} aria-hidden /> : <RefreshCw size={14} aria-hidden />} Possession
          </button>
        </div>
        {!readOnly && (
          <button type="button" className={styles.primaryBtn} onClick={() => setEditing('new')}>
            <Plus size={14} aria-hidden /> Nouveau pays
          </button>
        )}
      </div>

      <FilterChips<FilterKey> items={chips} value={filter} onChange={setFilter} label="Filtrer les pays" />

      {possession && !possession.available && (
        <InfoBox tone="warn" title="Possession en jeu indisponible">
          {possession.message ?? 'NGCountryAPI injoignable'}. La colonne « En jeu » affiche « ? ». Le satellite relit la
          possession à chaque suppression : tant qu'elle reste illisible, supprimer un pays le désactive seulement.
        </InfoBox>
      )}

      {!readOnly && selectedCountries.length > 0 && (
        <div className={ws.selectionBar} role="region" aria-label="Sélection">
          <CheckCircle2 size={16} aria-hidden />
          <span className={ws.selectionBarText}>
            <strong>{fmtInt(selectedCountries.length)}</strong> pays sélectionné{selectedCountries.length > 1 ? 's' : ''}
            {hiddenSelected > 0 && <> dont <strong>{fmtInt(hiddenSelected)}</strong> hors de la vue</>}
          </span>
          <button type="button" className={styles.primaryBtn} onClick={() => setBulkOpen(true)}>
            <Tags size={14} aria-hidden /> Associer à une région
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={() => setSelected(new Set())}>
            <X size={14} aria-hidden /> Désélectionner
          </button>
        </div>
      )}

      {/* Table défilante (en-tête collant, barre horizontale visible) ; Actions collée à droite. */}
      <div className={cx(ws.tableWrap, ws.tableScroll)}>
        <table className={cx(ws.table, cs.denseTable)}>
          <thead>
            <tr>
              {!readOnly && (
                <th className={cs.checkCol}>
                  <input type="checkbox" checked={allVisibleSelected} disabled={visible.length === 0}
                    ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                    onChange={toggleAll} aria-label={allVisibleSelected ? 'Tout désélectionner' : 'Sélectionner les pays affichés'}
                    title={allVisibleSelected ? 'Tout désélectionner' : 'Sélectionner les pays affichés'} />
                </th>
              )}
              <th>Drapeau</th>
              <th>Pays (FR)</th>
              <th>Pays (EN)</th>
              <th><span className={cs.thHelp} title="Nom porté une fois devenu empire : FR, puis EN en dessous">Empire (FR / EN)</span></th>
              <th><span className={cs.thHelp} title="Point d'apparition posé par /f setdhome : sans lui, le pays n'est pas fondable">Centre (f home)</span></th>
              <th>Actif</th>
              <th>Région</th>
              <th className={ws.num}>Chunks</th>
              <th><span className={cs.thHelp} title="Possession lue en direct dans NGCountryAPI">En jeu</span></th>
              <th className={cx(cs.actionsCol, ws.stickyEnd)}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const region = c.geoRegionId ? regionById.get(c.geoRegionId) : undefined;
              const pos: InGame = possession?.available
                ? inGame(possessionByName.get(c.slug))
                : { text: '?', tone: 'off', icon: CircleHelp, title: possession?.message ?? 'Possession non chargée' };
              const rowBusy = busy?.id === c.id ? busy.what : null;
              const checked = selected.has(c.id);
              const hasHome = c.centerX != null && c.centerZ != null;
              const PosIcon = pos.icon;
              return (
                <tr key={c.id} className={cx(!c.enabled && ws.rowOff, checked && ws.rowSel)}>
                  {!readOnly && (
                    <td className={cs.checkCol}>
                      <input type="checkbox" checked={checked} onChange={() => toggleOne(c.id)} aria-label={`Sélectionner ${c.slug}`} />
                    </td>
                  )}
                  <td><FlagThumb flag={c.flag} width={48} /></td>
                  <td>
                    <span className={cs.nameCell}>
                      <ColorChip color={c.color} size={20} title={c.color} />
                      <span className={cs.slug}>{c.slug}</span>
                      {c.isoCode && <span className={ws.iso} title="Code ISO">{c.isoCode}</span>}
                    </span>
                  </td>
                  <td>{c.slugEn ? <span className={ws.mono}>{c.slugEn}</span> : <Dash />}</td>
                  <td>
                    {c.empireNameFr || c.empireNameEn ? (
                      <span className={cs.stack}>
                        {c.empireNameFr ? <span className={ws.mono} title="Empire (FR)">{c.empireNameFr}</span> : <Dash />}
                        {c.empireNameEn
                          ? <span className={cx(ws.mono, cs.stackSub)} title="Empire (EN)">{c.empireNameEn}</span>
                          : <span className={cs.stackSub}><Dash /></span>}
                      </span>
                    ) : <Dash />}
                  </td>
                  <td>
                    {hasHome ? (
                      <span className={cs.centerCell}>
                        <span className={ws.mono}>{c.centerX}, {c.centerZ}</span>
                        {c.centerInside === false && (
                          <StatusBadge tone="warn" icon={TriangleAlert} title="Le f home est hors des chunks du pays sur le niveau affiché">
                            hors territoire
                          </StatusBadge>
                        )}
                      </span>
                    ) : (
                      <span className={cs.none} title="Aucun f home : pays non proposé à la fondation">aucun</span>
                    )}
                  </td>
                  <td>
                    {readOnly ? (
                      <StatusBadge tone={c.enabled ? 'ok' : 'off'} icon={c.enabled ? Eye : EyeOff}>{c.enabled ? 'Actif' : 'Inactif'}</StatusBadge>
                    ) : (
                      <StatusBadge tone={c.enabled ? 'ok' : 'off'} icon={c.enabled ? Eye : EyeOff}
                        busy={rowBusy === 'toggle'} disabled={rowBusy != null} onClick={() => toggleEnabled(c)}
                        title={c.enabled
                          ? 'Désactiver : retire le pays du roster fondable et masque ses frontières'
                          : 'Réactiver le pays'}>
                        {c.enabled ? 'Actif' : 'Inactif'}
                      </StatusBadge>
                    )}
                  </td>
                  <td>
                    {region
                      ? <TintBadge color={region.color} title={`${region.name} (${region.slug})`}>{region.name}</TintBadge>
                      : <Dash />}
                  </td>
                  <td className={ws.num}>{c.chunkCount > 0 ? <span className={ws.strongNum}>{fmtInt(c.chunkCount)}</span> : <Dash />}</td>
                  <td><StatusBadge tone={pos.tone} icon={PosIcon} title={pos.title}>{pos.text}</StatusBadge></td>
                  <td className={cx(cs.actionsCol, ws.stickyEnd)}>
                    <span className={cs.rowActions}>
                      <ActionButton icon={MapIcon} tone="primary" title="Voir sur la carte" onClick={() => onShowOnMap(c.id)} />
                      <ActionButton icon={readOnly ? Eye : Pencil} tone="primary" title={readOnly ? 'Voir la fiche' : 'Modifier'}
                        onClick={() => setEditing(c)} />
                      {!readOnly && (
                        <>
                          <ActionButton icon={MapPinOff} tone="warn" busy={rowBusy === 'center'} disabled={!hasHome || rowBusy != null}
                            title={hasHome ? 'Supprimer le f home' : 'Aucun f home à supprimer'} onClick={() => void clearCenter(c)} />
                          <ActionButton icon={Trash2} tone="danger" busy={rowBusy === 'delete'} disabled={rowBusy != null}
                            title="Supprimer le pays" onClick={() => void remove(c)} />
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (!loaded ? (
              <EmptyRow colSpan={colSpan} icon={Loader2} spin title="Chargement des pays…" />
            ) : countries.length === 0 ? (
              <EmptyRow colSpan={colSpan} icon={Flag} title="Aucun pays"
                text={readOnly
                  ? 'Aucun pays n\'est configuré pour l\'instant.'
                  : 'Crée le premier pays, ou lance « Import NG Java » dans l\'en-tête pour reprendre ceux de NationsGlory Java.'}
                actions={!readOnly && (
                  <button type="button" className={styles.primaryBtn} onClick={() => setEditing('new')}>
                    <Plus size={14} aria-hidden /> Nouveau pays
                  </button>
                )} />
            ) : (
              <EmptyRow colSpan={colSpan} icon={SearchX} title="Aucun pays ne correspond"
                text={`Aucun des ${fmtInt(countries.length)} pays pour ${[
                  q.trim() && `la recherche « ${q.trim()} »`,
                  activeFilter && `le filtre « ${activeFilter.label} »`,
                ].filter(Boolean).join(' et ')}.`}
                actions={filtered && (
                  <button type="button" className={styles.secondaryBtn} onClick={clearFilters}>
                    <X size={14} aria-hidden /> Effacer les filtres
                  </button>
                )} />
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CountryModal country={editing === 'new' ? null : editing} geoRegions={geoRegions} readOnly={readOnly}
          onClose={() => setEditing(null)}
          onSaved={(c) => {
            const before = editing === 'new' ? null : editing;
            putCountry(c);
            if (!before || before.geoRegionId !== c.geoRegionId) void reloadGeoRegions();
          }} />
      )}

      {bulkOpen && selectedCountries.length > 0 && (
        <BulkRegionModal countries={selectedCountries} geoRegions={geoRegions}
          onClose={() => setBulkOpen(false)} onDone={onBulkDone} onGoRegions={goRegions} />
      )}
    </div>
  );
}
